// src/pages/AdLogin.tsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api, { setAccessToken } from '../../../shared/api/axios';
import { useAuth } from '../../../shared/contexts/AuthContext';
import HighLogo from "../../HighLogo";
import logo from "../../../assets/logo.png";
import Modal from "../../settings/Modal";
import StartFooter from "../StartFooterAd";

const AdLogin = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { setToken, setUser, setE2EEPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // ---- 2FA UI state ----
  const [twoFAOpen, setTwoFAOpen] = useState(false);
  const [temp2FAToken, setTemp2FAToken] = useState<string | null>(null);
  const [otp, setOtp] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [emailFocused, setEmailFocused] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);


async function finalizeLoginAndGoHome(accessToken: string) {
  setAccessToken(accessToken);
  setToken(accessToken);

  const profileRes = await api.get('/users/me');
  const profile = profileRes.data;

  setUser(profile);
  setE2EEPassword(password);

  const advertiser = profile.advertiserProfile;

  if (!advertiser || advertiser.completed !== true) {
    navigate('/adregistration', { replace: true });
    return;
  }

  navigate('/advertisementstart', { replace: true });
}
  
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
  
    try {
      const response = await api.post("/auth/login", { email, password });
  
      // 2FA required
      if (response?.data?.twoFARequired && response?.data?.token) {
        const temp = response.data.token as string;
        setTemp2FAToken(temp);
        setTwoFAOpen(true);
  
        try {
          await api.post("/security/2fa/email/request-login", { token: temp });
        } catch (reqErr: any) {
          setError(
            reqErr?.response?.data?.msg ||
            reqErr?.message ||
            t("twoFA.requestFailed")
          );
        }
        return;
      }
  
      // Normal login
      const accessToken = response?.data?.accessToken || response?.data?.token;
      if (!accessToken) throw new Error("No access token returned from server.");
  
      // ✅ Do it once
      await finalizeLoginAndGoHome(accessToken);
    } catch (err: any) {
      const message =
        err.response?.data?.msg ||
        err.message ||
        t("startPage.invalidCredentials");
      setError(message);
    }
  };

  // When user submits the 6-digit code
  const handleVerify2FA = async () => {
    setBusy(true);
    setError(null);
  
    try {
      if (!temp2FAToken) throw new Error(t("twoFA.missingToken"));
      if (!/^\d{6}$/.test(otp.trim())) {
        setError(t("twoFA.invalidCode"));
        return;
      }
  
      const verifyRes = await api.post("/security/2fa/email/verify-login", {
        token: temp2FAToken,
        otp: otp.trim(),
      });
  
      const realAccessToken = verifyRes?.data?.accessToken || verifyRes?.data?.token;
      if (!realAccessToken) throw new Error("No auth token returned.");
  
      // ✅ Call once
      await finalizeLoginAndGoHome(realAccessToken);
  
      setTwoFAOpen(false);
      setOtp("");
      setTemp2FAToken(null);
    } catch (err: any) {
      console.error("2FA verify error:", err);
      setError(
        err?.response?.data?.msg ||
        err?.message ||
        t("twoFA.verifyFailed")
      );
    } finally {
      setBusy(false);
    }
  };

const handleForgotPassword = () => {
  navigate('/ad-forgot-password');
};

  const handleCreateAccount = () => navigate('/register');

  return (
    <div
      style={{
        minHeight: '100vh',
        width: '100%',
        background:
          'radial-gradient(circle at top, #020617 0, #000 40%, #020617 100%)',
        color: '#f9fafb',
        fontFamily:
          'system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
        display: 'flex',
        flexDirection: 'column',
      }}
    >


 <div style={floatingLogoStyle}>
  <button
    type="button"
    onClick={() => navigate('/ads-home')}
    aria-label="Back to advertiser home"
    style={floatingLogoButtonStyle}
  >
    <HighLogo
      src={logo}
      style={{
        width: 190,
        display: 'block',
      }}
      padding={0}
    />
  </button>
</div>

  
      {/* Centered login "window" */}
<main style={mainStyle}>
  <section style={phoneSide}>
    <div style={phoneFrame}>
      <div style={phoneSpeaker} />
      <div style={phoneScreen}>
        <div style={videoPlaceholder}>
          <div style={playCircle}>▶</div>
          <div style={videoTitle}>Welcome back</div>
          <div style={videoText}>Your login video or image will appear here.</div>
        </div>
      </div>
    </div>
  </section>

<section style={loginPanel}>

  <div style={loginHeading}>
<h1 style={titleStyle}>
  <span style={{ color: "#f8fafc" }}>
    Login
  </span>{" "}

  <span
    style={{
      background:
        "linear-gradient(135deg, #f97316 0%, #facc15 100%)",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
    }}
  >
    ELNYDO Ads
  </span>
</h1>
  </div>

  {!!error && <div style={errorBox}>{error}</div>}

  <form onSubmit={handleLogin} style={formStyle}>
<div style={floatingField}>
  <label
    style={{
      ...floatingLabel,
      top: emailFocused || email ? 9 : 18,
      fontSize: emailFocused || email ? 11 : 16,
      color: emailFocused ? "#60A5FA" : "#94A3B8",
    }}
  >
    {t("email")}
  </label>

  <input
    type="email"
    value={email}
    onChange={(e) => setEmail(e.target.value)}
    onFocus={() => setEmailFocused(true)}
    onBlur={() => setEmailFocused(false)}
    style={floatingInput}
    autoComplete="email"
    required
  />
</div>

<div style={floatingField}>
  <label
    style={{
      ...floatingLabel,
      top: passwordFocused || password ? 9 : 18,
      fontSize: passwordFocused || password ? 11 : 16,
      color: passwordFocused ? "#60A5FA" : "#94A3B8",
    }}
  >
    {t("password")}
  </label>

  <input
    type="password"
    value={password}
    onChange={(e) => setPassword(e.target.value)}
    onFocus={() => setPasswordFocused(true)}
    onBlur={() => setPasswordFocused(false)}
    style={floatingInput}
    autoComplete="current-password"
    required
  />
</div>

    <button type="submit" style={submitBtn}>
      {t('login')}
    </button>

    <button
      type="button"
      onClick={handleForgotPassword}
      style={forgotLink}
    >
      {t('forgotPassword')}
    </button>

    <button
      type="button"
      onClick={handleCreateAccount}
      style={createBtn}
    >
      {t('createAccount')}
    </button>
  </form>
</section>
</main>

<StartFooter />

  
      {/* 2FA modal (unchanged) */}
      <Modal
        open={twoFAOpen}
        onClose={() => {
          if (!busy) {
            setTwoFAOpen(false);
            setOtp('');
          }
        }}
        title={t('twoFA.title')}
        width={380}
        closeOnOverlayClick={false}
      >
        <>
          <p style={{ marginTop: 0, fontSize: 14 }}>
            {t('twoFA.intro')}
          </p>
  
          <input
            inputMode="numeric"
            pattern="\d*"
            value={otp}
            onChange={(e) =>
              setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))
            }
            placeholder={t('twoFA.placeholder')}
            maxLength={6}
            style={modalInputStyle}
            autoFocus
          />
  
          <div
            style={{
              display: 'flex',
              gap: 8,
              marginTop: 12,
              justifyContent: 'flex-end',
            }}
          >
            <button
              type="button"
              onClick={() => {
                setTwoFAOpen(false);
                setOtp('');
              }}
              disabled={busy}
              style={{
                padding: '10px 14px',
                borderRadius: 8,
                border: 'none',
                background: '#4b5563',
                color: '#ccc',
                cursor: busy ? 'default' : 'pointer',
                opacity: busy ? 0.7 : 1,
              }}
            >
              {t('twoFA.cancel')}
            </button>
            <button
              type="button"
              onClick={handleVerify2FA}
              disabled={busy || otp.trim().length !== 6}
              style={{
                padding: '10px 14px',
                borderRadius: 8,
                border: 'none',
                background: '#16a34a',
                color: '#ccc',
                cursor:
                  busy || otp.trim().length !== 6 ? 'default' : 'pointer',
                opacity: busy || otp.trim().length !== 6 ? 0.7 : 1,
              }}
            >
              {busy ? t('twoFA.verifying') : t('twoFA.verify')}
            </button>
          </div>
  
          <div style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>
            {t('twoFA.hint')}
          </div>
        </>
      </Modal>
    </div>
  );
  
  
};


const loginPanel: React.CSSProperties = {
  position: "relative",
  width: "100%",
  maxWidth: 560,
  padding: "20px 0",
  boxSizing: "border-box",

  transform: "translate(35px, -30px)",
};

const loginHeading: React.CSSProperties = {
  marginBottom: 50,
};

const titleStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: 560,
  margin: 0,
  color: "#f8fafc",
  fontSize: 38,
  lineHeight: 1.06,
  letterSpacing: "-1.7px",
  fontWeight: 800,
  marginBottom: 30,

};



const formStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: 500,
  display: "flex",
  flexDirection: "column",
  gap: 20,
};


const submitBtn: React.CSSProperties = {
  width: "100%",
  padding: "13px 18px",
  marginTop: 12,
  borderRadius: 999,
  border: "none",
  background:
    "linear-gradient(135deg, rgba(212,175,55,1), rgba(250,250,210,0.95))",
  color: "#111827",
  cursor: "pointer",
  fontSize: 15,
  fontWeight: 800,
  boxShadow: "0 12px 30px rgba(212,175,55,0.18)",
};

const forgotLink: React.CSSProperties = {
  alignSelf: "center",
  padding: 0,
  border: "none",
  background: "transparent",
  color: "#93c5fd",
  cursor: "pointer",
  fontSize: 14,
  marginTop: 8
};

const createBtn: React.CSSProperties = {
  width: "100%",
  padding: "14px 18px",
  borderRadius: 999,
  border: "1px solid rgba(163,230,53,0.32)",
  background: "rgba(34,197,94,0.08)",
  color: "#bef264",
  cursor: "pointer",
  fontSize: 15,
  fontWeight: 700,
  marginTop: 25
};

const modalInputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px',
  marginTop: 6,
  borderRadius: 8,
  border: '1px solid #ccc',
  fontSize: 16,
  boxSizing: 'border-box',
};

const mainStyle: React.CSSProperties = {
  flex: 1,
  width: "100%",
  maxWidth: 1240,
  margin: "0 auto",
  display: "grid",
  gridTemplateColumns:
    "minmax(280px, 340px) minmax(420px, 560px)",
  alignItems: "center",
  justifyContent: "center",
  columnGap: "clamp(50px, 7vw, 90px)",
  padding: "38px clamp(24px, 4vw, 48px) 64px",
  boxSizing: "border-box",
};


const errorBox: React.CSSProperties = {
  marginBottom: 12,
  padding: 10,
  borderRadius: 12,
  color: "#fed7aa",
  background: "rgba(127,29,29,0.3)",
  border: "1px solid rgba(248,113,113,0.6)",
  fontSize: 13,
};


const phoneSide: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transform: "translateX(20px)",
};

const phoneFrame: React.CSSProperties = {
  width: 285,
  height: 585,
  borderRadius: 42,
  padding: 12,
  background: "linear-gradient(145deg, #111827, #020617)",
  border: "1px solid rgba(248,250,252,0.18)",
  boxShadow:
    "0 35px 90px rgba(0,0,0,0.85), inset 0 0 0 1px rgba(255,255,255,0.08)",
  position: "relative",
};

const phoneSpeaker: React.CSSProperties = {
  position: "absolute",
  top: 18,
  left: "50%",
  transform: "translateX(-50%)",
  width: 78,
  height: 6,
  borderRadius: 999,
  background: "rgba(148,163,184,0.45)",
  zIndex: 2,
};

const phoneScreen: React.CSSProperties = {
  width: "100%",
  height: "100%",
  borderRadius: 32,
  overflow: "hidden",
  background:
    "radial-gradient(circle at top, rgba(212,175,55,0.28), rgba(15,23,42,1) 40%, #000 100%)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const videoPlaceholder: React.CSSProperties = {
  textAlign: "center",
  padding: 24,
  color: "#e5e7eb",
};

const playCircle: React.CSSProperties = {
  width: 64,
  height: 64,
  borderRadius: "50%",
  margin: "0 auto 18px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "rgba(212,175,55,0.95)",
  color: "#111827",
  fontSize: 24,
  fontWeight: 800,
};

const videoTitle: React.CSSProperties = {
  fontSize: 18,
  fontWeight: 800,
  marginBottom: 8,
};

const videoText: React.CSSProperties = {
  fontSize: 12,
  color: "#94a3b8",
  lineHeight: 1.5,
};


const floatingLogoStyle: React.CSSProperties = {
  width: "100%",
  padding: "28px 20px 0",
  boxSizing: "border-box",
};

const floatingLogoButtonStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  padding: 0,
  margin: 0,
  border: "none",
  background: "transparent",
  cursor: "pointer",
};

const floatingField: React.CSSProperties = {
  position: "relative",
  width: "100%",
};

const floatingLabel: React.CSSProperties = {
  position: "absolute",
  left: 18,
  transition: "all .18s ease",
  pointerEvents: "none",
  fontWeight: 500,
};

const floatingInput: React.CSSProperties = {
  width: "100%",
  height: 58,
  padding: "24px 18px 0",
  borderRadius: 16,
  border: "1px solid rgba(148,163,184,0.25)",
  background: "rgba(15,23,42,0.45)",
  color: "#f8fafc",
  fontSize: 16,
  outline: "none",
  boxSizing: "border-box",
  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.03)",
  lineHeight: "58px",
};


export default AdLogin;
